import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PivotTableUI from 'react-pivottable/PivotTableUI';
import 'react-pivottable/pivottable.css';
import TableRenderers from 'react-pivottable/TableRenderers';
import Plot from 'react-plotly.js';
import createPlotlyRenderers from 'react-pivottable/PlotlyRenderers';
import {
  Box,
  Typography,
  IconButton,
  Alert,
  AppBar,
  Button,
  Stack,
  Toolbar,
  TextField,
  Chip,
  Autocomplete,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import BarChartIcon from '@mui/icons-material/BarChart';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const PlotlyRenderers = createPlotlyRenderers(Plot);

const BillingPivotTablePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  
  const locationState = location.state || {};
  const { activities = [], selectedActivities = [] } = locationState;

// Estado para campos seleccionados
  const [selectedFields, setSelectedFields] = useState([
    'Pagador',
    'Descripción',
    'Monto Split (€)',
    'Mes',
    'Año'
  ]);

  useEffect(() => {
    console.log('📊 PIVOT PAGE - Datos recibidos:', {
      hasLocationState: !!location.state,
      activitiesLength: activities.length,
      selectedLength: selectedActivities.length,
      firstActivity: activities[0]
    });
    
    if (activities.length === 0) {
      console.error('❌ No se recibieron actividades en el pivot');
    }
  }, [activities, selectedActivities, location.state]);

  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('findDOMNode')) {
        return;
      }
      originalError.call(console, ...args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);

  const getStatusLabel = (status) => {
    const statuses = {
      'PENDING': 'Pendiente',
      'IN_ORDER': 'En Orden',
      'INVOICED': 'Facturado',
      'PAID': 'Pagado',
      'CANCELLED': 'Cancelado'
    };
    return statuses[status] || 'Desconocido';
  };

const pivotData = useMemo(() => {
  console.log('🔄 Calculando pivotData:', {
    totalActivities: activities.length,
    selectedCount: selectedActivities.length,
    willFilter: selectedActivities.length > 0
  });

  if (!Array.isArray(activities) || activities.length === 0) {
    console.warn('⚠️ No hay actividades para procesar');
    return [];
  }

  let dataToUse = activities;
  
  if (selectedActivities?.length > 0) {
    dataToUse = activities.filter(a => {
      const id = a?._id;
      const isSelected = selectedActivities.includes(id);
      return a && id && isSelected;
    });
    console.log('✅ Actividades filtradas:', dataToUse.length);
  } else {
    console.log('✅ Usando todas las actividades:', activities.length);
  }

  const expandedRows = [];

  dataToUse.forEach(activity => {
    if (!activity) return;

    // ===== DATOS BASE =====
    let mes = 'Sin Fecha';
    let año = 'Sin Año';
    let trimestre = 'Sin Trimestre';
    
    if (activity.service_date) {
      try {
        const date = new Date(activity.service_date);
        const month = date.getMonth();
        mes = date.toLocaleString('es-ES', { month: 'long', year: 'numeric' });
        año = date.getFullYear().toString();
        trimestre = `Q${Math.floor(month / 3) + 1} ${año}`;
      } catch (e) {
        console.warn('Error parseando fecha:', e);
      }
    }

    const netAmount = parseFloat(activity.total_amount) || 0;
    const unitPrice = parseFloat(activity.unit_price) || 0;
    const quantity = parseInt(activity.quantity) || 1;
    const subtotal = parseFloat(activity.subtotal) || 0;
    const discountAmount = parseFloat(activity.discount_amount) || 0;
    const taxAmount = parseFloat(activity.tax_amount) || 0;
    const taxPercentage = parseFloat(activity.tax_percentage) || 0;

    const patientData = activity.patient_id || activity.patient_complete || {};
    const patientName = patientData.fullName || 
                        `${patientData.name || ''} ${patientData.surname || ''}`.trim() || 
                        activity.patient_name || 
                        'Sin Paciente';
    
    const baseRow = {
      'ID Actividad': activity._id || 'N/A',
      'Código': activity.code || 'N/A',
      'Paciente': patientName,
      'Email Paciente': patientData.email || activity.patient_email || 'Sin Email',
      'Teléfono Paciente': patientData.phone || 'Sin Teléfono',
      'Dirección Paciente': patientData.address || 'Sin Dirección',
      'Ciudad Paciente': patientData.city || 'Sin Ciudad',
      'Código Postal': patientData.zipcode || 'Sin CP',
      'ID Paciente': patientData._id || activity.patient_id || 'N/A',
      'Custom ID Paciente': patientData.customid || 'N/A',
      'Médico': activity.doctor_name || 'Sin Médico',
      'Centro': activity.center_name || activity.company_id || 'Sin Centro',
      'Descripción': activity.description || 'Sin Descripción',
      'Notas Internas': activity.internal_notes || 'Sin Notas',
      'Fecha': activity.service_date || 'Sin Fecha',
      'Mes': mes,
      'Año': año,
      'Trimestre': trimestre,
      'Fecha Realización': activity.performed_at || 'N/A',
      'Fecha Creación': activity.createdAt || 'N/A',
      'Última Actualización': activity.updatedAt || 'N/A',
      'Tipo de Actividad': activity.activity_type || 'Otro',
      'Estado Facturación': getStatusLabel(activity.billing_status),
      'Estado Raw': activity.billing_status || 'UNKNOWN',
      'Precio Unitario (€)': unitPrice,
      'Cantidad': quantity,
      'Subtotal (€)': subtotal,
      'Descuento (€)': discountAmount,
      'IVA %': taxPercentage,
      'IVA (€)': taxAmount,
      'Monto Total (€)': netAmount,
      'Número de Actividades': 1,
    };

    // ===== PROCESAR SPLITS - FILTRAR NULOS =====
    if (activity.split && Array.isArray(activity.split) && activity.split.length > 0) {
      activity.split.forEach((splitItem, splitIndex) => {
        // 🔥 FILTRAR: Solo agregar si tiene pagador válido
        const payerName = splitItem.payer_snapshot?.display_name || 
                         splitItem.payer_snapshot?.name || 
                         splitItem.payer_name;
        
        // ⚠️ CONDICIÓN CRÍTICA: Saltar si no hay pagador o es null/undefined/vacío
        if (!payerName || 
            payerName.trim() === '' || 
            payerName.toLowerCase() === 'null' ||
            payerName.toLowerCase() === 'undefined') {
          console.log('⏭️ Saltando split sin pagador válido:', splitItem);
          return; // Saltar este split
        }

        const splitRow = {
          ...baseRow,
          'Tiene Split': 'Sí',
          'Número de Split': splitIndex + 1,
          'Total Splits': activity.split.length,
          'Pagador': payerName,
          'Tipo Pagador': splitItem.payer_type || 'Sin Tipo',
          'Descripción Pagador': splitItem.payer_snapshot?.type_description || 
                                 splitItem.description || 
                                 'Sin Descripción',
          'Porcentaje Split (%)': splitItem.percent || 0,
          'Monto Máximo Split (€)': parseFloat(splitItem.max_amount) || 0,
          'Monto Split (€)': (netAmount * (splitItem.percent / 100)) || 0,
          'ID Pagador': splitItem.payer_id || 'N/A',
          'ID Split': splitItem._id || 'N/A',
        };

        expandedRows.push(splitRow);
      });
    }

    // ===== PROCESAR SALES ORDERS =====
    if (activity.so_links && Array.isArray(activity.so_links) && activity.so_links.length > 0) {
      activity.so_links.forEach((soLink, soIndex) => {
        const soDetails = activity.sales_orders_details?.find(
          so => so._id === soLink.sales_order_id || so.sales_order_id === soLink.sales_order_id
        );

        const soRow = {
          ...baseRow,
          'Tiene SO': 'Sí',
          'Número de SO': soIndex + 1,
          'Total SOs': activity.so_links.length,
          'ID Sales Order': soLink.sales_order_id || 'N/A',
          'Monto SO (€)': parseFloat(soLink.amount) || 0,
          'Porcentaje SO (%)': parseFloat(soLink.percent) || 0,
          'Número Orden': soDetails?.order_number || 'N/A',
          'Estado Orden': soDetails?.order_status || 'UNKNOWN',
          'Fecha Creación SO': soDetails?.created_at || 'N/A',
          'Fecha Exportación Odoo': soDetails?.odoo_exported_at || 'N/A',
          'ID Odoo': soDetails?.odoo_order_id || 'N/A',
          'Exportado a Odoo': soDetails?.odoo_order_id ? 'Sí' : 'No',
          'Total Lines SO': soDetails?.lines?.length || 0,
        };

        expandedRows.push(soRow);
      });
    }
  });

  // 🔥🔥🔥 FILTRO FINAL: ELIMINAR CUALQUIER FILA CON VALORES NULL/UNDEFINED EN CUALQUIER CAMPO
  const cleanedRows = expandedRows.filter(row => {
    // Verificar cada valor de la fila
    for (const [key, value] of Object.entries(row)) {
      // Si el valor es null, undefined, o la cadena "null"/"undefined"
      if (value === null || 
          value === undefined || 
          (typeof value === 'string' && 
           (value.toLowerCase() === 'null' || value.toLowerCase() === 'undefined'))) {
        console.log(`⏭️ Eliminando fila con ${key} = ${value}`);
        return false; // Excluir esta fila
      }
    }
    return true; // Mantener esta fila
  });

  console.log('✅ Pivot Data expandido:', cleanedRows.length, 'filas generadas (sin nulls)');
  console.log('📊 Filas eliminadas:', expandedRows.length - cleanedRows.length);
  console.log('📊 Muestra de datos:', cleanedRows[0]);
  
  return cleanedRows;
}, [activities, selectedActivities]);


// TODOS los campos posibles
  const allAvailableFields = useMemo(() => [
    'ID Actividad', 'Código', 'Paciente', 'Email Paciente', 'Teléfono Paciente',
    'Dirección Paciente', 'Ciudad Paciente', 'Código Postal', 'ID Paciente',
    'Custom ID Paciente', 'Médico', 'Centro', 'Descripción', 'Notas Internas',
    'Fecha', 'Mes', 'Año', 'Trimestre', 'Fecha Realización', 'Fecha Creación',
    'Última Actualización', 'Tipo de Actividad', 'Estado Facturación', 'Estado Raw',
    'Precio Unitario (€)', 'Cantidad', 'Subtotal (€)', 'Descuento (€)', 'IVA %',
    'IVA (€)', 'Monto Total (€)', 'Número de Actividades', 'Tiene Split',
    'Número de Split', 'Total Splits', 'Pagador', 'Tipo Pagador', 'Descripción Pagador',
    'Porcentaje Split (%)', 'Monto Máximo Split (€)', 'Monto Split (€)', 'ID Pagador',
    'ID Split', 'Tiene SO', 'Número de SO', 'Total SOs', 'ID Sales Order',
    'Monto SO (€)', 'Porcentaje SO (%)', 'Número Orden', 'Estado Orden',
    'Fecha Creación SO', 'Fecha Exportación Odoo', 'ID Odoo', 'Exportado a Odoo',
    'Total Lines SO'
  ], []);

  // Campos que NO están seleccionados (para el selector)
  const availableFieldsForSelector = useMemo(() => {
    return allAvailableFields.filter(field => !selectedFields.includes(field));
  }, [allAvailableFields, selectedFields]);

// ===== ACTUALIZAR pivotState inicial =====
const [pivotState, setPivotState] = useState({
  rows: ['Pagador'],
  cols: ['Descripción'],
  aggregatorName: 'Sum',
  vals: ['Monto Split (€)'],
  rendererName: 'Table Heatmap'
});

  const attrValues = useMemo(() => {
    if (pivotData.length === 0) {
      const emptyValues = {};
      selectedFields.forEach(field => {
        emptyValues[field] = [];
      });
      return emptyValues;
    }
    
    const values = {};
    
    selectedFields.forEach(field => {
      const uniqueValues = new Set();
      pivotData.forEach(row => {
        if (row[field] !== undefined && row[field] !== null) {
          uniqueValues.add(row[field]);
        }
      });
      values[field] = Array.from(uniqueValues).sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') {
          return a - b;
        }
        return String(a).localeCompare(String(b));
      });
    });
    
    return values;
  }, [pivotData, selectedFields]);

  const resetPivot = () => {
    setPivotState({
      rows: ['Paciente'],
      cols: ['Estado'],
      aggregatorName: 'Sum',
      vals: ['Monto Total (€)'],
      rendererName: 'Table'
    });
  };

const handleAddField = (event, newValue) => {
    if (newValue && !selectedFields.includes(newValue)) {
      setSelectedFields([...selectedFields, newValue]);
    }
  };

  const handleRemoveField = (fieldToRemove) => {
    setSelectedFields(selectedFields.filter(field => field !== fieldToRemove));
  };

  const cleanCellValue = (text) => {
    if (!text) return '';
    return text
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/^\s*€\s*/, '')
      .replace(/\s*€\s*$/, '');
  };

  const handleExport = (format = 'csv') => {
    try {
      if (pivotData.length === 0) {
        alert('No hay datos para exportar');
        return;
      }

      const pivotTable = document.querySelector('.pvtTable');
      if (!pivotTable) {
        alert('No se puede exportar. Asegúrate de que la tabla esté visible.');
        return;
      }

      if (format === 'csv') {
        exportToCSV(pivotTable);
      } else if (format === 'pdf') {
        exportToPDF(pivotTable);
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert(`Error al exportar a ${format.toUpperCase()}: ${error.message}`);
    }
  };

  const exportToCSV = (table) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    const csvData = [];
    
    rows.forEach(row => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      const rowData = [];
      
      cells.forEach(cell => {
        const colspan = parseInt(cell.getAttribute('colspan')) || 1;
        let value = cleanCellValue(cell.textContent);
        
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        
        rowData.push(value);
        
        for (let i = 1; i < colspan; i++) {
          rowData.push('');
        }
      });
      
      if (rowData.some(v => v !== '')) {
        csvData.push(rowData);
      }
    });
    
    const csv = csvData.map(row => row.join(',')).join('\n');
    const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '-');
    downloadFile(csv, `tabla_pivote_${timestamp}.csv`, 'text/csv');
  };

  const exportToPDF = (table) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Tabla Dinámica de Actividades Facturables', 14, 15);
    
    if (stats) {
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100);
      doc.text(
        `${stats.count} actividades | Total: ${stats.totalAmount.toFixed(2)}€`,
        14, 22
      );
    }
    
    const rows = Array.from(table.querySelectorAll('tr'));
    const tableData = [];
    
    rows.forEach(row => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      const rowData = [];
      
      cells.forEach(cell => {
        const colspan = parseInt(cell.getAttribute('colspan')) || 1;
        let value = cleanCellValue(cell.textContent);
        rowData.push(value);
        for (let i = 1; i < colspan; i++) {
          rowData.push('');
        }
      });
      
      if (rowData.some(v => v !== '')) {
        tableData.push(rowData);
      }
    });
    
    if (tableData.length === 0) {
      alert('No hay datos para exportar');
      return;
    }
    
    autoTable(doc, {
      head: [tableData[0]],
      body: tableData.slice(1),
      startY: 28,
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 8
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { top: 28, left: 10, right: 10, bottom: 15 },
      theme: 'grid',
      didDrawPage: function(data) {
        doc.setFontSize(8);
        doc.setTextColor(150);
        const pageCount = doc.internal.pages.length - 1;
        doc.text(
          `Página ${data.pageNumber} de ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
        
        doc.text(
          new Date().toLocaleString('es-ES'),
          doc.internal.pageSize.width - 10,
          doc.internal.pageSize.height - 10,
          { align: 'right' }
        );
      }
    });
    
    const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '-');
    doc.save(`tabla_pivote_${timestamp}.pdf`);
  };

  const downloadFile = (content, filename, mimeType) => {
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + content], { type: `${mimeType};charset=utf-8;` });
    const link = document.createElement('a');
    
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  const stats = useMemo(() => {
    if (pivotData.length === 0) return null;
    const totalAmount = pivotData.reduce((sum, row) => sum + (row['Monto Total (€)'] || 0), 0);
    return {
      totalAmount,
      count: pivotData.length
    };
  }, [pivotData]);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      width: '100vw',
      bgcolor: '#f5f5f5',
      overflow: 'hidden'
    }}>
      <AppBar position="static" elevation={2} style={{ backgroundColor: "#ff6347" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => history.goBack()}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <BarChartIcon sx={{ mr: 1 }} />
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              {t("Pivot Table - Billable Activities")}
            </Typography>
            {stats && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {stats.count} actividades • Total: {stats.totalAmount.toFixed(2)}€
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1}>
            <IconButton 
              color="inherit" 
              onClick={resetPivot} 
              title={t("Reset View")}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              color="inherit"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('csv')}
              disabled={pivotData.length === 0}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              CSV
            </Button>
            <Button
              color="inherit"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('pdf')}
              disabled={pivotData.length === 0}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              PDF
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        flex: 1,
        width: '100%',
        overflow: 'auto',
        position: 'relative',
        p: 2
      }}>
        {pivotData.length === 0 ? (
          <Alert severity="warning">
            <Typography variant="body2" gutterBottom>
              <strong>No hay datos disponibles</strong>
            </Typography>
            <Typography variant="caption">
              Verifica que haya actividades cargadas en la tabla principal.
            </Typography>
          </Alert>
        ) : (
          <>
            <style>
              {`
                .pvtUi {
                  display: grid !important;
                  grid-template-columns: 300px 1fr !important;
                  grid-template-rows: auto auto 1fr !important;
                  gap: 16px !important;
                  width: 100% !important;
                  min-height: calc(100vh - 140px) !important;
                  box-sizing: border-box !important;
                }

                .pvtRenderers {
                  grid-column: 1 !important;
                  grid-row: 1 !important;
                  padding: 12px !important;
                  background: #f8f8f8 !important;
                  border: 1px solid #ddd !important;
                  border-radius: 8px !important;
                }

                .pvtRenderers select {
                  width: 100% !important;
                  padding: 10px !important;
                  border: 1px solid #ccc !important;
                  border-radius: 6px !important;
                  font-size: 14px !important;
                  background-color: white !important;
                  cursor: pointer !important;
                }

                .pvtAxisContainer, .pvtVals {
                  grid-column: 1 !important;
                  grid-row: 2 !important;
                  padding: 12px !important;
                  background-color: #f8f8f8 !important;
                  border: 1px solid #ddd !important;
                  border-radius: 8px !important;
                  min-height: 80px !important;
                }

                .pvtUnused {
                  grid-column: 1 !important;
                  grid-row: 3 !important;
                  padding: 12px !important;
                  background-color: #ffffff !important;
                  border: 1px solid #ddd !important;
                  border-radius: 8px !important;
                  overflow-y: auto !important;
                  overflow-x: hidden !important;
                  max-height: 400px !important;
                }

                .pvtUnused::before {
                  content: "📋 Campos Disponibles" !important;
                  display: block !important;
                  font-size: 13px !important;
                  font-weight: 600 !important;
                  color: #333 !important;
                  margin-bottom: 12px !important;
                  padding-bottom: 10px !important;
                  border-bottom: 2px solid #e0e0e0 !important;
                }

                .pvtAttr {
                  margin: 5px 4px !important;
                  padding: 8px 14px !important;
                  display: inline-block !important;
                  font-size: 12px !important;
                  font-weight: 500 !important;
                  background: #ff6347 !important;
                  color: white !important;
                  border: none !important;
                  border-radius: 6px !important;
                  cursor: grab !important;
                  transition: all 0.2s ease !important;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
                }

                .pvtAttr:hover {
                  transform: translateY(-2px) !important;
                  box-shadow: 0 4px 8px rgba(0,0,0,0.2) !important;
                }

                .pvtUiCell {
                  grid-column: 2 !important;
                  grid-row: 1 / 4 !important;
                  background-color: white !important;
                  border-radius: 8px !important;
                  padding: 0 !important;
                  border: 1px solid #ddd !important;
                  overflow: hidden !important;
                  display: flex !important;
                  flex-direction: column !important;
                  min-height: 600px !important;
                }

                .pvtOutput {
                  width: 100% !important;
                  height: 100% !important;
                  overflow: auto !important;
                  padding: 16px !important;
                  box-sizing: border-box !important;
                }

                .pvtTable {
                  font-size: 13px !important;
                  border-collapse: collapse !important;
                  width: auto !important;
                  min-width: 100% !important;
                  background: white !important;
                }

                .pvtTable th, .pvtTable td {
                  padding: 12px 16px !important;
                  border: 1px solid #e0e0e0 !important;
                  text-align: left !important;
                  white-space: nowrap !important;
                }

                .pvtTable thead tr th {
                  background: #ff6347 !important;
                  color: white !important;
                  font-weight: 600 !important;
                  position: sticky !important;
                  top: 0 !important;
                  z-index: 10 !important;
                  text-transform: uppercase !important;
                  font-size: 11px !important;
                  letter-spacing: 0.5px !important;
                }

                .pvtTable tbody tr:nth-child(even) {
                  background-color: #f9f9f9 !important;
                }

                .pvtTable tbody tr:hover {
                  background-color: #e3f2fd !important;
                  transition: background-color 0.2s ease !important;
                }

                .pvtTotal, .pvtGrandTotal {
                  background-color: #fff3cd !important;
                  font-weight: 700 !important;
                }

                .pvtOutput::-webkit-scrollbar,
                .pvtUnused::-webkit-scrollbar {
                  width: 12px !important;
                  height: 12px !important;
                }

                .pvtOutput::-webkit-scrollbar-track,
                .pvtUnused::-webkit-scrollbar-track {
                  background: #f1f1f1 !important;
                  border-radius: 6px !important;
                }

                .pvtOutput::-webkit-scrollbar-thumb,
                .pvtUnused::-webkit-scrollbar-thumb {
                  background: #ff6347 !important;
                  border-radius: 6px !important;
                }

                .pvtOutput::-webkit-scrollbar-thumb:hover,
                .pvtUnused::-webkit-scrollbar-thumb:hover {
                  background: #ff6347 !important;
                }
              `}
            </style>
            {/* NUEVO SELECTOR DE CAMPOS */}
            <Paper elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#f8f8f8' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                📋 Seleccionar Campos
              </Typography>
              
              <Autocomplete
                options={availableFieldsForSelector}
                onChange={handleAddField}
                value={null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscar y agregar campo..."
                    size="small"
                    sx={{ mb: 1 }}
                  />
                )}
                noOptionsText="No hay campos disponibles"
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {selectedFields.map(field => (
                  <Chip
                    key={field}
                    label={field}
                    onDelete={() => handleRemoveField(field)}
                    color="primary"
                    sx={{
                      backgroundColor: '#ff6347',
                      '&:hover': { backgroundColor: '#ff4500' }
                    }}
                  />
                ))}
              </Box>

              {selectedFields.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Selecciona al menos un campo para trabajar con la tabla pivote
                </Alert>
              )}
            </Paper>

            {/* PIVOT TABLE */}
            <PivotTableUI
              data={pivotData}
              onChange={s => setPivotState(s)}
              renderers={Object.assign({}, TableRenderers, PlotlyRenderers)}
              {...pivotState}
              attrValues={attrValues}
              hiddenFromDragDrop={allAvailableFields.filter(f => !selectedFields.includes(f))}
              locale="es"
              plotlyOptions={{
                displayModeBar: true,
                displaylogo: false,
                modeBarButtonsToRemove: ['lasso2d', 'select2d']
              }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default BillingPivotTablePage;